/**
 * Agregasi papan monitoring pengiriman — MURNI.
 *
 * Papan ini SENGAJA tanpa peta real-time (ADR-0036 §D1). Yang dijawabnya bukan "di mana kurir
 * sekarang", melainkan "berapa banyak yang macet, dan karena apa" — pertanyaan yang benar-benar
 * menghasilkan tindakan dispatcher, dan yang tidak menuntut pelacakan berkelanjutan.
 */

import type { ReasonCodeLite } from './delivery-form';

export interface BoardTask {
  readonly status: string;
}

export interface BoardAttempt {
  readonly failureReasonCodeId: string | null;
}

/**
 * Satu-satunya tempat yang tahu nilai enum `outcome` yang sebenarnya (`'succeeded'`/`'failed'`,
 * huruf kecil — ADR-0035). Sebelum fungsi ini ada, tiga layar membandingkan `outcome` dengan
 * literal `'SUCCESS'` (huruf besar, tidak pernah cocok) langsung di JSX — akibatnya SETIAP
 * percobaan tampil "Gagal" apa pun hasil sebenarnya. `outcome: string` yang longgar membuat
 * TypeScript tidak menangkapnya. Bandingkan lewat sini, jangan literal ulang di pemanggil.
 */
export function isSuccessfulAttempt(outcome: 'succeeded' | 'failed'): boolean {
  return outcome === 'succeeded';
}

export interface StatusCount {
  readonly status: string;
  readonly label: string;
  readonly count: number;
}

/** Kolom papan, dalam urutan alur kerja — bukan urutan abjad. */
const BOARD_STATUSES: readonly { readonly status: string; readonly label: string }[] = [
  { status: 'assigned', label: 'Ditugaskan' },
  { status: 'accepted', label: 'Diterima' },
  { status: 'started', label: 'Berjalan' },
  { status: 'arrived', label: 'Sampai lokasi' },
  { status: 'completed', label: 'Selesai' },
  { status: 'failed', label: 'Gagal' },
];

/**
 * Hitungan per status.
 *
 * Kolom bernilai nol TETAP ditampilkan. Kolom yang hilang saat kosong membuat papan berubah
 * bentuk sepanjang hari, dan dispatcher kehilangan titik acuan — "tidak ada yang gagal" adalah
 * informasi, bukan alasan menghapus kolomnya.
 */
export function countByStatus(tasks: readonly BoardTask[]): readonly StatusCount[] {
  return BOARD_STATUSES.map(({ status, label }) => ({
    status,
    label,
    count: tasks.filter((t) => t.status === status).length,
  }));
}

export interface ReasonBreakdown {
  readonly reasonCodeId: string | null;
  readonly label: string;
  readonly count: number;
}

/**
 * Sebaran alasan gagal, terbanyak lebih dulu.
 *
 * Memakai `reasonCodeLabel` agar kode yang sudah dinonaktifkan tetap terbaca — kegagalan
 * bulan lalu tidak berhenti bermakna hanya karena katalognya berubah.
 */
export function breakdownByReason(
  attempts: readonly BoardAttempt[],
  codes: readonly ReasonCodeLite[],
): readonly ReasonBreakdown[] {
  const counts = new Map<string | null, number>();
  for (const attempt of attempts) {
    counts.set(attempt.failureReasonCodeId, (counts.get(attempt.failureReasonCodeId) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([reasonCodeId, count]) => ({
      reasonCodeId,
      label: labelFor(codes, reasonCodeId),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'id'));
}

function labelFor(codes: readonly ReasonCodeLite[], reasonCodeId: string | null): string {
  if (reasonCodeId === null) return 'Tanpa alasan tercatat';
  const found = codes.find((c) => c.id === reasonCodeId);
  if (!found) return 'Alasan tidak dikenal';
  return found.isActive ? found.label : `${found.label} (tidak dipakai lagi)`;
}

/**
 * Attempt terurut menurut `attempt_no`.
 *
 * Percobaan kedua TIDAK menimpa yang pertama — keduanya baris tersendiri. Riwayat percobaan
 * adalah bukti bahwa kurir sudah datang dua kali; menampilkan hanya yang terakhir menghapus
 * kerja yang sudah dilakukan.
 */
export function sortAttempts<A extends { readonly attemptNo: number }>(
  attempts: readonly A[],
): readonly A[] {
  return [...attempts].sort((a, b) => a.attemptNo - b.attemptNo);
}
